import type { IFormActualsTargetsSettings } from 'legacy/lut/classes/models/partner360/IFormActualsTargetsSettings'
import { getMandatoryLinesInCategoryCount } from 'legacy/lut/components/widgets/public/partner360/planning/helpers/partnerFlags/getMandatoryLinesInCategoryCount'
import type { IClientSectionSettings } from 'legacy/lut/features/partner360/sectionSettings/models/iSectionSettingsState'

import { isSubCategoryMandatory } from 'partner360/entities/planning'

import { OVERALL_SUB_CATEGORY } from '../model/constants'
import type { SobTableEntry } from '../model/types'

import { flattenSobTableEntries } from './sobInputState'
import { buildSobSettingsCategoryKey, buildSobSettingsKey } from './sobSettingsKeys'

export type NoPlanStateByKey = Record<string, boolean>
export type NoPlanSelector = (entry: SobTableEntry) => boolean
export type NoPlanEligibility = {
  show: boolean
  disabled: boolean
}

type CreateNoPlanEligibilityResolverParams = {
  offering: string
  /** `false` keeps every eligible `no plan` checkbox visible but read-only */
  isNoPlanEditable: boolean
  partnerFlags: string
  sectionSettings: Partial<IClientSectionSettings>
  sobAvailableSettingsByKey: Map<string, IFormActualsTargetsSettings>
  sobSettingsByCategoryKey: Map<string, IFormActualsTargetsSettings[]>
  allSobAvailableTableEntries: SobTableEntry[]
  getEntryNoPlan: (entry: SobTableEntry) => boolean
  getEntryNoPlanContractual: (entry: SobTableEntry) => boolean
}

export const selectEntryNoPlan: NoPlanSelector = (entry) => entry.model.isNoPlan
export const selectEntryNoPlanContractual: NoPlanSelector = (entry) =>
  entry.model.isNoPlanContractual

export const buildInitialNoPlanByKey = (
  entries: SobTableEntry[],
  selectNoPlan: NoPlanSelector
): NoPlanStateByKey =>
  flattenSobTableEntries(entries).reduce<NoPlanStateByKey>((acc, entry) => {
    acc[entry.key] = selectNoPlan(entry)

    return acc
  }, {})

export const buildNextNoPlanByKey = (
  entries: SobTableEntry[],
  currentNoPlanByKey: NoPlanStateByKey,
  selectNoPlan: NoPlanSelector
): NoPlanStateByKey =>
  flattenSobTableEntries(entries).reduce<NoPlanStateByKey>((acc, entry) => {
    acc[entry.key] = currentNoPlanByKey[entry.key] ?? selectNoPlan(entry)

    return acc
  }, {})

export const createNoPlanEligibilityResolver = ({
  offering,
  isNoPlanEditable,
  partnerFlags,
  sectionSettings,
  sobAvailableSettingsByKey,
  sobSettingsByCategoryKey,
  allSobAvailableTableEntries,
  getEntryNoPlan,
  getEntryNoPlanContractual,
}: CreateNoPlanEligibilityResolverParams) => {
  const tableEntriesBySettingsKey = new Map(
    allSobAvailableTableEntries.map((entry) => [
      buildSobSettingsKey({
        offering,
        category: entry.category,
        subCategory: entry.subCategory,
        l3Category: entry.l3Category,
      }),
      entry,
    ])
  )

  const getEntrySobAvailabilitySetting = (entry: SobTableEntry) =>
    sobAvailableSettingsByKey.get(
      buildSobSettingsKey({
        offering,
        category: entry.category,
        subCategory: entry.subCategory,
        l3Category: entry.l3Category,
      })
    )

  const getEntrySobAvailabilitySettingsInCategory = (entry: SobTableEntry) =>
    sobSettingsByCategoryKey.get(
      buildSobSettingsCategoryKey({
        offering,
        category: entry.category,
      })
    ) ?? []

  const getSettingTableEntry = (setting: IFormActualsTargetsSettings) =>
    tableEntriesBySettingsKey.get(
      buildSobSettingsKey({
        offering: setting.ProgramType,
        category: setting.Category,
        subCategory: setting.SubCategory,
        l3Category: setting.L3Category,
      })
    )

  const getSettingIsForcedExpanded = (setting: IFormActualsTargetsSettings) => {
    try {
      const settings = JSON.parse(setting.Settings || '{}') as {
        ExpandedByFlags?: string[]
      }

      return Boolean(
        settings.ExpandedByFlags?.length &&
          isSubCategoryMandatory(
            partnerFlags,
            settings.ExpandedByFlags.join(','),
            sectionSettings,
            true
          ).isMandatory
      )
    } catch {
      return false
    }
  }

  const getSettingMandatoryCount = (
    setting: IFormActualsTargetsSettings,
    formats: IFormActualsTargetsSettings[]
  ) =>
    getMandatoryLinesInCategoryCount({
      partnerFlags,
      formats,
      currentFormat: setting,
      sectionSettings,
      isUnits: false,
      restrictL3Child: '',
    })

  const getIsSettingMandatory = (setting: IFormActualsTargetsSettings, includeUnits: boolean) => {
    const isMandatoryByFlags = isSubCategoryMandatory(
      partnerFlags,
      setting.MandatoryByFlags,
      sectionSettings,
      true
    ).isMandatory

    if (isMandatoryByFlags || !includeUnits) {
      return isMandatoryByFlags
    }

    return isSubCategoryMandatory(
      partnerFlags,
      setting.UnitsMandatoryByFlags,
      sectionSettings,
      true
    ).isMandatory
  }

  const getSubCategoriesWithPlanCount = (entry: SobTableEntry, isContractual: boolean) => {
    const currentSetting = getEntrySobAvailabilitySetting(entry)

    if (!currentSetting) {
      return 0
    }

    const formats = getEntrySobAvailabilitySettingsInCategory(entry)
    const curSubCat = currentSetting.SubCategory
    const curL3Cat = currentSetting.L3Category
    const curIsL3Overall = curL3Cat === OVERALL_SUB_CATEGORY
    const curIsL3 = Boolean(curL3Cat)
    const curIsL3Child = curIsL3 && !curIsL3Overall

    return formats
      .filter(
        (setting) =>
          (!curIsL3Child &&
            setting.SubCategory !== OVERALL_SUB_CATEGORY &&
            Boolean(setting.SubCategory) &&
            (!setting.L3Category || setting.L3Category === OVERALL_SUB_CATEGORY)) ||
          (curIsL3Child &&
            setting.SubCategory === curSubCat &&
            setting.L3Category !== OVERALL_SUB_CATEGORY &&
            Boolean(setting.L3Category))
      )
      .reduce((result, setting) => {
        const l3Cat = setting.L3Category
        const isL3Overall = l3Cat === OVERALL_SUB_CATEGORY
        const isL3 = Boolean(l3Cat)
        const isL3Child = isL3 && !isL3Overall
        const mandatoryInChildCount = isL3Overall
          ? getMandatoryLinesInCategoryCount({
              partnerFlags,
              formats,
              currentFormat: setting,
              sectionSettings,
              isUnits: false,
              restrictL3Child: setting.SubCategory,
            })
          : 0

        if (
          (isL3 && !isL3Child && mandatoryInChildCount) ||
          !getIsSettingMandatory(setting, true)
        ) {
          return result
        }

        const settingEntry = getSettingTableEntry(setting)

        if (!settingEntry) {
          return result
        }

        const isSettingNoPlan = isContractual
          ? getEntryNoPlanContractual(settingEntry)
          : getEntryNoPlan(settingEntry)

        return isSettingNoPlan ? result : result + 1
      }, 0)
  }

  return (entry: SobTableEntry, isContractual: boolean): NoPlanEligibility => {
    if (entry.isOverall) {
      return { show: false, disabled: false }
    }

    const setting = getEntrySobAvailabilitySetting(entry)

    if (!setting) {
      return { show: false, disabled: false }
    }

    const currentNoPlanValue = isContractual
      ? getEntryNoPlanContractual(entry)
      : getEntryNoPlan(entry)
    const formats = getEntrySobAvailabilitySettingsInCategory(entry)
    const isShowMandatory = getIsSettingMandatory(setting, false)
    const mandatoryInCatCount = getSettingMandatoryCount(setting, formats)
    const show =
      isShowMandatory &&
      mandatoryInCatCount > 1 &&
      !getSettingIsForcedExpanded(setting) &&
      (!isContractual || Boolean(setting.IsContractualsAvailable) || currentNoPlanValue)
    const withPlanCount = getSubCategoriesWithPlanCount(entry, isContractual)

    return {
      show,
      disabled: !isNoPlanEditable || (!currentNoPlanValue && withPlanCount <= 1),
    }
  }
}
